import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Fallback if the /lead-sources API isn't reachable — mirrors the historical
// hardcoded set so the dropdown never renders empty.
const DEFAULT_LEAD_SOURCES = [
  "Manual", "Referral", "Google Ads", "Facebook", "Yelp", "Nextdoor",
  "Thumbtack", "Home Advisor", "Angi", "Website", "Repeat Customer",
  "Drive-by", "Door Hanger", "Other",
];

export default function CustomerDialog({ open, onOpenChange, customer, onSuccess }) {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    address: "",
    notes: "",
    source: ""
  });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [leadSources, setLeadSources] = useState([]);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Fetch admin-configurable Lead Source options. If the endpoint fails
  // (e.g. non-admin viewing the dialog), fall back to the historical
  // hardcoded set so the dropdown is never empty.
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('session_token');
    axios
      .get(`${API}/lead-sources`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const active = (res.data || []).filter((s) => s.enabled !== false).map((s) => s.label);
        setLeadSources(active.length > 0 ? active : DEFAULT_LEAD_SOURCES);
      })
      .catch(() => setLeadSources(DEFAULT_LEAD_SOURCES));
  }, [open]);

  const LEAD_SOURCES = leadSources;

  useEffect(() => {
    if (customer) {
      setFormData({
        fullName: customer.fullName || "",
        phoneNumber: customer.phoneNumber || "",
        email: customer.email || "",
        address: customer.address || "",
        notes: customer.notes || "",
        source: customer.source || ""
      });
    } else {
      setFormData({
        fullName: "",
        phoneNumber: "",
        email: "",
        address: "",
        notes: "",
        source: ""
      });
    }
    setPhoneError("");
  }, [customer, open]);

  // Initialize Google Maps Autocomplete
  useEffect(() => {
    if (!open) return;
    
    // Small delay to ensure the DOM element is mounted
    const timeoutId = setTimeout(() => {
      if (!addressInputRef.current) {
        console.log("Address input ref not available");
        return;
      }
      
      const initAutocomplete = () => {
        // Check if Google Maps API is loaded
        if (!window.google?.maps?.places) {
          console.log("Google Maps Places API not loaded yet");
          return false;
        }

        // Clean up previous instance
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }

        try {
          // Initialize autocomplete
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            addressInputRef.current,
            {
              componentRestrictions: { country: "us" },
              fields: ["formatted_address", "address_components"],
              types: ["address"]
            }
          );

          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();
            if (place?.formatted_address) {
              setFormData(prev => ({ ...prev, address: place.formatted_address }));
            }
          });
          
          console.log("Google Maps Autocomplete initialized successfully");
          return true;
        } catch (error) {
          console.error("Failed to initialize autocomplete:", error);
          return false;
        }
      };
      
      // If Google Maps is already ready, initialize immediately
      if (window.googleMapsReady || window.google?.maps?.places) {
        initAutocomplete();
      } else {
        // Wait for Google Maps to load via callback
        const originalCallback = window.onGoogleMapsReady;
        window.onGoogleMapsReady = () => {
          if (originalCallback) originalCallback();
          initAutocomplete();
        };
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [open]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phoneNumber: formatted });
    
    // Validate - must be exactly 10 digits
    const digits = formatted.replace(/\D/g, "");
    if (digits.length > 0 && digits.length !== 10) {
      setPhoneError("Phone number must be 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const validatePhone = () => {
    const digits = formData.phoneNumber.replace(/\D/g, "");
    return digits.length === 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePhone()) {
      setPhoneError("Phone number must be 10 digits");
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (customer) {
        await axios.put(
          `${API}/customers/${customer.customer_id}`,
          formData,
          { headers }
        );
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, formData, { headers });
        toast.success("Customer created");
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="customer-dialog" onPointerDownOutside={(e) => {
        // Allow clicks on Google Places Autocomplete dropdown
        if (e.target.closest('.pac-container')) {
          e.preventDefault();
        }
      }} onInteractOutside={(e) => {
        // Prevent dialog from closing when clicking autocomplete suggestions
        if (e.target.closest('.pac-container')) {
          e.preventDefault();
        }
      }}>
        <DialogHeader>
          <DialogTitle className="font-barlow text-2xl uppercase">
            {customer ? "Edit Customer" : "New Customer"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {customer ? "Edit customer information" : "Add a new customer"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName" className="font-inter font-semibold text-zinc-700">
              Full Name *
            </Label>
            <Input
              id="fullName"
              data-testid="customer-fullname-input"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
              className="h-12 mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phoneNumber" className="font-inter font-semibold text-zinc-700">
              Phone Number * <span className="text-xs text-zinc-400 font-normal">(10 digits)</span>
            </Label>
            <Input
              id="phoneNumber"
              data-testid="customer-phone-input"
              type="tel"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="(951) 555-1234"
              required
              className={`h-12 mt-1 ${phoneError ? "border-red-500 focus:border-red-500" : ""}`}
            />
            {phoneError && (
              <p className="text-red-500 text-sm mt-1">{phoneError}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email" className="font-inter font-semibold text-zinc-700">
              Email
            </Label>
            <Input
              id="email"
              data-testid="customer-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="customer@example.com"
              className="h-12 mt-1"
            />
          </div>

          <div>
            <Label htmlFor="address" className="font-inter font-semibold text-zinc-700">
              Address
            </Label>
            <Input
              id="address"
              ref={addressInputRef}
              data-testid="customer-address-input"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Start typing to search..."
              className="h-12 mt-1"
            />
            <p className="text-xs text-zinc-400 mt-1">Start typing for address suggestions</p>
          </div>

          <div>
            <Label htmlFor="source" className="font-inter font-semibold text-zinc-700">
              Lead Source
            </Label>
            <Select
              value={formData.source}
              onValueChange={(value) => setFormData({ ...formData, source: value })}
            >
              <SelectTrigger className="h-12 mt-1" data-testid="customer-source-select">
                <SelectValue placeholder="Select source (optional)" />
              </SelectTrigger>
              <SelectContent>
                {LEAD_SOURCES.map((src) => (
                  <SelectItem key={src} value={src}>
                    {src}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="notes" className="font-inter font-semibold text-zinc-700">
              Notes
            </Label>
            <Textarea
              id="notes"
              data-testid="customer-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="customer-cancel-button"
              className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !!phoneError}
              data-testid="customer-submit-button"
              className="h-12 flex-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50"
            >
              {loading ? "Saving..." : customer ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
